import { Box, Flex, Text, IconButton } from "@chakra-ui/react";
import { format, getWeek, isToday, isSameDay, endOfWeek } from "date-fns";
import { formatMinutes, deLocaleOption } from "@/data/DateTimeUtil";
import { Holiday } from "@/api/Holiday";
import { Timesheet as TimesheetEntry } from "@/api/Timesheet";
import { Absence } from "@/api/Absence";
import { TimesheetData, timesheetDataMap } from "../TimesheetData";
import { DeleteIcon, AddIcon } from "@/components/icons/Icon";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/ui/tag";
import IdAndNameData from "@/data/IdAndNameData";
import { ApprovalStatusUtil } from "@/data/ApprovalStatusUtil";
import { LuCheck } from "react-icons/lu";
import { HolidayDetails } from "./HolidayDetails";

interface TimesheetCellProps {
  hover: boolean;
  day: Date;
  dailyEntries: TimesheetEntry[];
  holiday: Holiday | undefined;
  absences: Absence[];
  projects: Map<number, string>;
  onEditEntry: (entry: TimesheetEntry) => void;
  onDeleteEntry: (data: IdAndNameData<number>) => void;
  onApproveEntry: (entry: TimesheetEntry) => void;
  onNewEntry: (day: Date, entries: TimesheetEntry[]) => void;
  canEditEntry: (entry: TimesheetEntry) => boolean;
  canCancelEntry: (entry: TimesheetEntry) => boolean;
  canApproveEntry: (entry: TimesheetEntry) => boolean;
  canCreateEntry: boolean;
  actionsVisible?: boolean;
  visibleEntries?: number;
}

const TimesheetCell = ({
  hover,
  day,
  dailyEntries,
  holiday,
  absences,
  projects,
  onEditEntry,
  onDeleteEntry,
  onApproveEntry,
  onNewEntry,
  canEditEntry,
  canCancelEntry,
  canApproveEntry,
  canCreateEntry,
  actionsVisible = false,
  visibleEntries = 2,
}: TimesheetCellProps) => {
  const maxEntries = hover ? dailyEntries.length : visibleEntries;
  const formattedDate = format(day, "d, MMMM", deLocaleOption);
  const hasMoreEntries = dailyEntries.length > visibleEntries;
  const weekNumber = getWeek(day, deLocaleOption);
  const today = isToday(day);
  const sunday = isSameDay(day, endOfWeek(day, deLocaleOption));
  const dailyMinutes = dailyEntries.reduce((sum, e) => sum + e.minutes, 0);

  return (
    <>
      <Flex
        justify="space-between"
        align="center"
        gap="1"
        wrap="wrap"
        pr="1"
        bgColor={today ? "blue.500" : "none"}
        color={today ? "white" : "none"}
      >
        <Tag size="lg" my="0" py="0" shadow="sm" fontWeight="bold" variant="surface" color={today ? "red" : "none"} colorPalette="yellow" flexShrink={0}>
          {formattedDate}
        </Tag>
        {holiday && <Box minW={0}>
          <Text fontWeight="bold" fontSize=".8rem">{holiday.name}</Text>
          <HolidayDetails holiday={holiday} />
        </Box>}
        {absences.length > 0 && <Text fontWeight="bold" fontSize=".8rem" flexShrink={0}>Ferien 🌴</Text>}
        {sunday && <Text fontWeight="bold" textStyle="md" flexShrink={0}>W{weekNumber}</Text>}
        {dailyMinutes > 0 && <Text fontWeight="bold" textStyle="md" flexShrink={0}>{formatMinutes(dailyMinutes)} 🕗</Text>}
      </Flex>

      {dailyEntries.slice(0, maxEntries).map(entry => {
        const entryType: TimesheetData | undefined = timesheetDataMap.get(entry?.entryId);
        const projectName: string | undefined = projects.get(entry?.projectId);
        const canEdit = canEditEntry(entry);
        const canCancel = canCancelEntry(entry);
        const canApprove = canApproveEntry(entry);
        const canOpen = canEdit || canCancel || canApprove;
        const statusColor = ApprovalStatusUtil.color(entry.status);
        const isApproved = ApprovalStatusUtil.isApproved(entry.status);
        const isRejected = ApprovalStatusUtil.isRejected(entry.status);
        const showActionButtons = canCancel || canApprove;
        const actionButtonCount = Number(canCancel) + Number(canApprove);

        return (
          <Box
            key={entry.id}
            mt="1"
            p="0"
            borderRadius="sm"
            borderWidth="1px"
            borderLeftWidth="8px"
            borderColor={isApproved ? "green.400" : isRejected ? "orange.400" : "border"}
            borderLeftColor={entryType?.color ?? "gray"}
            bgColor={isApproved ? "green.subtle" : isRejected ? "orange.subtle" : "bg.muted"}
            color="fg"
            boxShadow="sm"
            position="relative"
            pr={actionsVisible && showActionButtons ? `${actionButtonCount * 2.25}rem` : "0"}
            cursor={canOpen ? "pointer" : "default"}
            _hover={{
              bgColor: canOpen ? "blue.600" : undefined,
              color: canOpen ? "white" : undefined,
              "& .entry-meta": { color: canOpen ? 'white' : undefined },
              boxShadow: "lg",
              "& .entry-action-button": { display: "flex", zIndex: 20 },
              "& .time-container": { bgColor: entryType?.color ?? "gray" }
            }}
            onClick={() => {
              if (canOpen) {
                onEditEntry(entry);
              }
            }}
          >
            <Flex direction="row" alignItems="center" justify="space-between" gap="2" minW={0}>
              <Flex className="time-container" direction="column" align="flex-start" p="0" m="0" bgColor="none" flexShrink={0}>
                <Text className='entry-meta' fontSize="xs" color="fg.muted">{entry.startTime}</Text>
                <Text className='entry-meta' fontSize="xs" color="fg.muted">{entry.endTime}</Text>
              </Flex>
              
              {projectName ? (
                <Flex flex="1" minW={0} justify="center">
                  <Text fontSize={entryType?.detail ? "xs" : "sm"} fontWeight="semibold" mt="1" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {projectName}
                  </Text>
                </Flex>
              ) : (
                <Flex flex="1" minW={0} direction="column" align="center">
                  <Text fontSize={entryType?.detail ? "xs" : "sm"} fontWeight="medium" mt="1" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxW="full">
                    {entryType?.name}
                  </Text>
                  <Text fontSize="xs" fontWeight="normal" mt="1" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxW="full">
                    {entryType?.detail}
                  </Text>
                </Flex>
              )}

              <Flex direction="column" align="flex-end" p="0" m="0" flexShrink={0}>
                <Text className='entry-meta' fontSize="xs" color="fg.muted">{formatMinutes(entry.minutes)}</Text>
                {entry.id > 0 && !ApprovalStatusUtil.isPending(entry.status) && (
                  <Tag size="sm" colorPalette={statusColor} mt="1">
                    {ApprovalStatusUtil.label(entry.status)}
                  </Tag>
                )}
              </Flex>
            </Flex>

            {canApprove && (
              <IconButton
                className="entry-action-button"
                aria-label="Approve entry"
                position="absolute"
                size="sm"
                top="0"
                right={canCancel ? "2.25rem" : "0"}
                display={actionsVisible ? "flex" : "none"}
                cursor="pointer"
                alignItems="center"
                justifyContent="center"
                onClick={(e) => {
                  e.stopPropagation();
                  onApproveEntry(entry);
                }}
                colorPalette="green"
              >
                <LuCheck />
              </IconButton>
            )}

            {canCancel && (
              <IconButton
                className="entry-action-button"
                aria-label="Cancel entry"
                position="absolute"
                size="sm"
                top="0"
                right="0"
                display={actionsVisible ? "flex" : "none"}
                cursor="pointer"
                alignItems="center"
                justifyContent="center"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteEntry({
                    id: entry.id,
                    name: `${entry.startTime} - ${entry.endTime}`
                  });
                }}
                colorPalette="red"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        );
      })}

      {!hover && hasMoreEntries && (
        <Text fontSize="xs" mt={1} color="fg.muted">
          +{dailyEntries.length - visibleEntries} mehr...
        </Text>
      )}

      {hover && canCreateEntry && (
        <Button
          mt="4px"
          w="full"
          colorPalette="blue"
          variant="outline"
          onClick={() => onNewEntry(day, dailyEntries)}
        >
          <AddIcon /> Neuer Eintrag
        </Button>
      )}
    </>
  );
};

export default TimesheetCell;
